import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowRightOnRectangleIcon, ChevronDownIcon, Bars3Icon, BellIcon } from '@heroicons/react/24/outline';
import { useDemo } from '../demo/DemoContext';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { useApiService } from '../hooks/useApiService';
import logger from '../utils/logger';
import TicketSubmitModal from './tickets/TicketSubmitModal';
import MyTicketsPanel from './tickets/MyTicketsPanel';

interface HeaderProps {
  user?: { username: string; role: string } | null;
  onLogout?: () => void;
  onMenuToggle?: () => void;
}

const ROLES = [
  { value: 'admin', label: '관리자', color: '#d93025' },
  { value: 'hr_manager', label: '매니저', color: '#1a73e8' },
  { value: 'org_head', label: '담당자', color: '#1e8e3e' },
  { value: 'employee', label: '조회자', color: '#80868b' },
] as const;

interface ViewAsOption {
  employee_id: string;
  name: string;
  position: string;
  org_name: string;
  unit_type: string;
  unit_label: string;
  access_scope: string;
  role: string;
}

const Header: React.FC<HeaderProps> = ({ user, onLogout, onMenuToggle }) => {
  const { isDemo, toggleDemo, roleOverride, setRoleOverride, viewAsEmployee, setViewAsEmployee } = useDemo();
  const effectiveRole = useEffectiveRole();
  const apiService = useApiService();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [viewAsOptions, setViewAsOptions] = useState<{ org_heads: ViewAsOption[]; employees: ViewAsOption[] } | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showMyTickets, setShowMyTickets] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin';
  const currentRoleInfo = ROLES.find(r => r.value === effectiveRole) || ROLES[3];
  const hasOverride = roleOverride !== null || viewAsEmployee !== null || isDemo;

  // Load view-as options when dropdown opens
  const loadViewAsOptions = useCallback(async () => {
    if (viewAsOptions) return;
    try {
      const data = await apiService.getViewAsOptions();
      setViewAsOptions(data);
    } catch { /* ignore */ }
  }, [apiService, viewAsOptions]);

  const loadAlertCount = useCallback(async () => {
    try {
      const resp = await apiService.getAlertCount();
      setAlertCount(resp?.unread_count || 0);
    } catch (e) { logger.warn('알림 카운트 로드 실패', e); }
  }, [apiService]);

  const loadAlerts = useCallback(async () => {
    try {
      const resp = await apiService.getAlerts(false, 20, 0);
      setAlerts(resp?.alerts || []);
      setAlertCount(resp?.unread_count || 0);
    } catch (e) { logger.warn('알림 목록 로드 실패', e); }
  }, [apiService]);

  useEffect(() => {
    loadAlertCount();
    const interval = setInterval(loadAlertCount, 60000);
    return () => clearInterval(interval);
  }, [loadAlertCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (alertRef.current && !alertRef.current.contains(e.target as Node)) {
        setAlertOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAlertToggle = () => {
    if (!alertOpen) loadAlerts();
    setAlertOpen(!alertOpen);
  };

  const handleMarkAllRead = async () => {
    try {
      await apiService.markAllAlertsRead();
      setAlertCount(0);
      setAlerts(prev => prev.map(a => ({ ...a, is_read: 1 })));
    } catch (e) { logger.error('알림 전체읽음 처리 실패', e); }
  };

  return (
    <>
      <header className="app-header" role="banner">
        <div className="app-header-content">
          <div className="header-left">
            <button className="mobile-menu-btn" onClick={onMenuToggle} aria-label="메뉴 열기">
              <Bars3Icon className="header-icon-md" />
            </button>
            <div className="header-logo-icon" aria-hidden="true">
              <svg className="header-icon-md" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1" fill="white"/>
                <rect x="14" y="3" width="7" height="7" rx="1" fill="white" fillOpacity="0.6"/>
                <rect x="3" y="14" width="7" height="7" rx="1" fill="white" fillOpacity="0.6"/>
                <rect x="14" y="14" width="7" height="7" rx="1" fill="white" fillOpacity="0.35"/>
              </svg>
            </div>
            <div>
              <h1 className="header-title">HR 관리</h1>
              <p className="header-subtitle">HR Intelligence</p>
            </div>
          </div>
          <div className="header-right">
            {/* 역할 전환 드롭다운 (admin 전용) */}
            {isAdmin && (
              <div ref={dropdownRef} className="header-dropdown-wrapper">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="header-role-btn"
                  style={{
                    borderColor: `${currentRoleInfo.color}40`,
                    background: `${currentRoleInfo.color}15`,
                    color: currentRoleInfo.color,
                  }}
                  aria-label="역할 전환"
                  aria-expanded={dropdownOpen}
                >
                  {currentRoleInfo.label}
                  <ChevronDownIcon className="header-icon-xs" />
                </button>
                {dropdownOpen && !viewAsOpen && (
                  <div className="header-dropdown">
                    <div className="header-dropdown-title">역할 전환</div>
                    {ROLES.map(role => (
                      <button
                        key={role.value}
                        onClick={() => {
                          if (role.value === 'org_head' || role.value === 'employee') {
                            setViewAsOpen(true);
                            loadViewAsOptions();
                            return;
                          }
                          setViewAsEmployee(null);
                          setRoleOverride(role.value === user?.role ? null : role.value);
                          setDropdownOpen(false);
                        }}
                        className={`header-dropdown-item ${effectiveRole === role.value && !viewAsEmployee ? 'active' : ''}`}
                      >
                        <span className="header-role-dot" style={{ background: role.color }} />
                        <span className="header-dropdown-item-label">{role.label}</span>
                        {role.value === 'org_head' && <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: '#80868b' }}>▸</span>}
                        {role.value === 'employee' && <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: '#80868b' }}>▸</span>}
                        {effectiveRole === role.value && !viewAsEmployee && (
                          <span className="header-check">✓</span>
                        )}
                        {role.value === user?.role && (
                          <span className="header-role-original">원래</span>
                        )}
                      </button>
                    ))}
                    {(roleOverride || viewAsEmployee) && (
                      <>
                        <div className="header-dropdown-divider" />
                        <button
                          onClick={() => { setRoleOverride(null); setViewAsEmployee(null); setDropdownOpen(false); }}
                          className="header-dropdown-reset"
                        >
                          원래 역할로 복귀
                        </button>
                      </>
                    )}
                  </div>
                )}
                {dropdownOpen && viewAsOpen && (
                  <div className="header-dropdown" style={{ width: 320, maxHeight: 480, overflowY: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e8eaed' }}>
                      <button
                        onClick={() => setViewAsOpen(false)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: '#5f6368', fontSize: '0.875rem' }}
                      >◂ 뒤로</button>
                      <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#202124' }}>View As — 담당자/조회자 선택</span>
                    </div>
                    {!viewAsOptions ? (
                      <div style={{ padding: 16, textAlign: 'center', color: '#80868b', fontSize: '0.8125rem' }}>로딩 중...</div>
                    ) : (
                      <>
                        {/* Org heads grouped by unit_type */}
                        {['company', 'division', 'team'].map(unitType => {
                          const items = viewAsOptions.org_heads.filter(o => o.unit_type === unitType);
                          if (items.length === 0) return null;
                          const groupLabel = unitType === 'company' ? '대표' : unitType === 'division' ? '본부장' : '팀장';
                          return (
                            <React.Fragment key={unitType}>
                              <div style={{ padding: '8px 12px 4px', fontSize: '0.6875rem', fontWeight: 600, color: '#80868b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {groupLabel}
                              </div>
                              {items.map(opt => (
                                <button
                                  key={opt.employee_id}
                                  onClick={() => {
                                    setViewAsEmployee(opt.employee_id);
                                    setRoleOverride('org_head');
                                    setDropdownOpen(false);
                                    setViewAsOpen(false);
                                  }}
                                  className={`header-dropdown-item ${viewAsEmployee === opt.employee_id ? 'active' : ''}`}
                                  style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                    <span className="header-role-dot" style={{ background: '#1e8e3e' }} />
                                    <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{opt.name}</span>
                                    <span style={{ fontSize: '0.6875rem', color: '#80868b', marginLeft: 'auto' }}>{opt.position}</span>
                                    {viewAsEmployee === opt.employee_id && <span className="header-check">✓</span>}
                                  </div>
                                  <div style={{ fontSize: '0.6875rem', color: '#5f6368', paddingLeft: 18 }}>
                                    {opt.org_name} · {opt.access_scope === 'own_team' ? '직속만' : '하위 전체'}
                                  </div>
                                </button>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        {/* Sample employees */}
                        {viewAsOptions.employees.length > 0 && (
                          <>
                            <div style={{ padding: '8px 12px 4px', fontSize: '0.6875rem', fontWeight: 600, color: '#80868b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              일반 조회자
                            </div>
                            {viewAsOptions.employees.map(opt => (
                              <button
                                key={opt.employee_id}
                                onClick={() => {
                                  setViewAsEmployee(opt.employee_id);
                                  setRoleOverride('employee');
                                  setDropdownOpen(false);
                                  setViewAsOpen(false);
                                }}
                                className={`header-dropdown-item ${viewAsEmployee === opt.employee_id ? 'active' : ''}`}
                                style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                  <span className="header-role-dot" style={{ background: '#80868b' }} />
                                  <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{opt.name}</span>
                                  <span style={{ fontSize: '0.6875rem', color: '#80868b', marginLeft: 'auto' }}>{opt.org_name}</span>
                                  {viewAsEmployee === opt.employee_id && <span className="header-check">✓</span>}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 데모 토글 (admin 전용) */}
            {isAdmin && (
              <button
                onClick={toggleDemo}
                className={`header-demo-btn ${isDemo ? 'active' : ''}`}
                aria-label={isDemo ? '데모 모드 끄기' : '데모 모드 켜기'}
              >
                <span className={`header-toggle-track ${isDemo ? 'active' : ''}`}>
                  <span className={`header-toggle-thumb ${isDemo ? 'active' : ''}`} />
                </span>
                <span className="header-demo-label">데모</span>
              </button>
            )}

            {/* Ticket System (MYB-357) — 이슈 등록 + 이슈 목록 */}
            <button
              onClick={() => setShowTicketModal(true)}
              className="header-issue-btn"
              aria-label="이슈 등록"
              title="이슈 등록"
            >
              <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="header-issue-label">이슈 등록</span>
            </button>
            <button
              onClick={() => setShowMyTickets(true)}
              className="header-issue-btn"
              aria-label="이슈 목록"
              title="이슈 목록"
            >
              <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="header-issue-label">이슈 목록</span>
            </button>

            {/* 알림 벨 */}
            <div ref={alertRef} style={{ position: 'relative' }}>
              <button
                onClick={handleAlertToggle}
                aria-label={`알림 ${alertCount}개`}
                style={{
                  position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 36, height: 36, border: 'none', borderRadius: '50%', cursor: 'pointer',
                  background: alertOpen ? '#e8f0fe' : 'transparent', color: '#5f6368',
                }}
              >
                <BellIcon style={{ width: 20, height: 20 }} />
                {alertCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18,
                    background: '#d93025', color: '#fff', borderRadius: 9,
                    fontSize: '0.6875rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', lineHeight: 1,
                  }}>{alertCount > 99 ? '99+' : alertCount}</span>
                )}
              </button>
              {alertOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  width: 380, maxHeight: 480, background: '#fff', borderRadius: '0.75rem',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.15)', border: '1px solid #e8eaed',
                  zIndex: 1000, overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', borderBottom: '1px solid #e8eaed',
                  }}>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#202124' }}>알림</span>
                    {alertCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        style={{ border: 'none', background: 'none', color: '#1a73e8', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 500 }}
                      >모두 읽음</button>
                    )}
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 420 }}>
                    {alerts.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: '#80868b', fontSize: '0.8125rem' }}>
                        새로운 알림이 없습니다
                      </div>
                    ) : (
                      alerts.map((alert: any) => (
                        <div
                          key={alert.id}
                          style={{
                            padding: '12px 16px', borderBottom: '1px solid #f1f3f4', cursor: 'pointer',
                            background: alert.is_read ? 'transparent' : '#e8f0fe20',
                          }}
                        >
                          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <span style={{
                              width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                              background: alert.severity === 'critical' ? '#d93025'
                                : alert.severity === 'warning' ? '#e37400' : '#1a73e8',
                            }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: '0.8125rem', fontWeight: alert.is_read ? 400 : 600,
                                color: '#202124', lineHeight: 1.4,
                              }}>{alert.title}</div>
                              <div style={{
                                fontSize: '0.75rem', color: '#5f6368', marginTop: 2,
                                lineHeight: 1.4, overflow: 'hidden',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                              }}>{alert.message}</div>
                              <div style={{ fontSize: '0.6875rem', color: '#80868b', marginTop: 4 }}>
                                {alert.created_at ? new Date(alert.created_at * 1000).toLocaleString('ko-KR') : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* MYB-413: User info, health badge, logout moved to sidebar LNB */}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </header>

      {/* Ticket Submit Modal (MYB-357) */}
      {showTicketModal && (
        <TicketSubmitModal
          defaultService="hr"
          onClose={() => setShowTicketModal(false)}
          onSuccess={() => {}}
        />
      )}

      {/* My Tickets Panel (MYB-357) */}
      {showMyTickets && (
        <MyTicketsPanel
          onClose={() => setShowMyTickets(false)}
          onNewTicket={() => { setShowMyTickets(false); setShowTicketModal(true); }}
        />
      )}

      {/* 오버라이드 배너 */}
      {hasOverride && (
        <div className="header-override-banner" role="status" aria-label="테스트 모드 활성화">
          <span>
            {isDemo && roleOverride && !viewAsEmployee && `데모 모드 · ${currentRoleInfo.label} 역할로 보는 중`}
            {isDemo && !roleOverride && !viewAsEmployee && '데모 모드 실행 중 — 가상 데이터입니다'}
            {viewAsEmployee && `View As: ${viewAsEmployee} (${currentRoleInfo.label}) — 해당 사용자의 권한으로 데이터를 조회합니다`}
            {!isDemo && roleOverride && !viewAsEmployee && `${currentRoleInfo.label} 역할로 보는 중`}
          </span>
          <button
            onClick={() => {
              if (isDemo) toggleDemo();
              if (roleOverride) setRoleOverride(null);
              if (viewAsEmployee) setViewAsEmployee(null);
            }}
            className="header-override-dismiss"
          >
            해제
          </button>
        </div>
      )}
    </>
  );
};

export default Header;
